import React from 'react';
import { PRIVACY_TEXT } from '../lib/privacy-content';
import { GERMAN_PRIVACY_TEXT } from '../lib/privacy-content-de';
import { useT, brandize } from '../i18n';
import { isDragonTiger, isGerman, isBrazil } from '../lib/app-variant';

// CF-GERMAN-LEGAL-TRANSLATE-V1 (PL #9155): the german (Münzo) build renders a
// fully-German Privacy Policy from a dedicated PURE content module
// (privacy-content-de.ts), NOT the English PRIVACY_TEXT patched at render time. This
// supersedes the rebrand's germanizeContacts() helper (now obsolete: GERMAN_PRIVACY_TEXT
// already carries the Münzo brand, the muenzo.xyz website, the support@muenzo.xyz contact,
// and 0 English). default + dragontiger keep the English PRIVACY_TEXT via brandize() — and
// the rendered output for those two is byte-identical to before, because the removed
// germanizeContacts() early-returned (identity) for any non-german build.

/**
 * CF-PRIVACY-PAGE-V1: /privacy page.
 *
 * Mirrors pages/Terms.tsx exactly (same dark shell, same bounded inline
 * markdown-to-JSX parser supporting only #/##/###, "- " lists, and blank lines
 * as paragraph breaks). No markdown lib, no HTML passthrough, no inline
 * emphasis — same scope discipline as CF-75.
 *
 * NOTE (follow-up): renderPolicy here is intentionally a copy of Terms.tsx's
 * renderTerms. A clean DRY-up — extract one shared lib/render-policy renderer
 * used by both Terms and Privacy — is tracked as a separate refactor so this
 * launch ship stays additive and does not modify the live /terms page.
 */

function renderPolicy(text: string): React.ReactNode[] {
    const lines = text.split('\n');
    const out: React.ReactNode[] = [];
    let para: string[] = [];
    let list: string[] = [];
    let key = 0;
    const flushPara = () => {
        if (para.length) {
            out.push(<p key={key++} style={{ margin: '0 0 1em 0', lineHeight: 1.65 }}>{para.join(' ')}</p>);
            para = [];
        }
    };
    const flushList = () => {
        if (list.length) {
            out.push(
                <ul key={key++} style={{ margin: '0 0 1em 1.5em', padding: 0, lineHeight: 1.65 }}>
                    {list.map((l, i) => <li key={i} style={{ marginBottom: '0.35em' }}>{l}</li>)}
                </ul>,
            );
            list = [];
        }
    };
    for (const rawLine of lines) {
        const line = rawLine.trimEnd();
        if (line.startsWith('### ')) {
            flushPara(); flushList();
            out.push(<h3 key={key++} style={{ color: 'var(--gold)', marginTop: '1.2em', marginBottom: '0.5em', fontSize: '0.95em' }}>{line.slice(4)}</h3>);
        } else if (line.startsWith('## ')) {
            flushPara(); flushList();
            out.push(<h2 key={key++} style={{ color: 'var(--gold)', marginTop: '1.6em', marginBottom: '0.6em', fontSize: '1.1em' }}>{line.slice(3)}</h2>);
        } else if (line.startsWith('# ')) {
            flushPara(); flushList();
            out.push(<h1 key={key++} style={{ color: '#fff', marginTop: '0.2em', marginBottom: '0.8em', fontSize: '1.4em' }}>{line.slice(2)}</h1>);
        } else if (line.startsWith('- ')) {
            flushPara();
            list.push(line.slice(2));
        } else if (line === '') {
            flushPara(); flushList();
        } else {
            flushList();
            para.push(line);
        }
    }
    flushPara(); flushList();
    return out;
}

export function PrivacyPage() {
    const t = useT();
    // German build → fully-German Privacy Policy (already brand-correct, 0 English);
    // default + dragontiger → English PRIVACY_TEXT folded to the variant brand by brandize().
    // TODO(P2/PL): MoedaFlip PT legal copy pending lawyer verbatim — the brazil build
    // intentionally falls back to the brandized English PRIVACY_TEXT (real legal) until the
    // PT Privacy Policy is authored, mirroring how dragontiger renders the English body.
    const body = isGerman ? GERMAN_PRIVACY_TEXT : brandize(PRIVACY_TEXT);
    return (
        <div
            style={{
                minHeight: '100vh',
                background: '#070b14',
                color: '#cbd2dd',
                padding: '24px 16px',
            }}
        >
            <div style={{ maxWidth: 720, margin: '0 auto', fontSize: 14 }}>
                <a
                    href="/"
                    style={{
                        display: 'inline-block',
                        color: 'var(--gold)',
                        textDecoration: 'none',
                        marginBottom: 16,
                        fontSize: 14,
                    }}
                >
                    {t('common.back_to_cricketflip')}
                </a>
                {/* dragontiger (zh) still renders the English legal body, so it keeps the
                    "English is the controlling version" notice. The german (Münzo) build
                    now renders a fully-German body (CF-GERMAN-LEGAL-TRANSLATE-V1), so the
                    notice no longer applies and is dropped for german. The brazil (MoedaFlip)
                    build renders the brandized English body (PT legal pending — see TODO
                    above), so it keeps the English-controlling notice too. */}
                {(isDragonTiger || isBrazil) && (
                    <div
                        style={{
                            border: '1px solid var(--gold)',
                            borderRadius: 8,
                            padding: '10px 12px',
                            margin: '0 0 18px 0',
                            fontSize: 13,
                            lineHeight: 1.6,
                            color: '#cbd2dd',
                            background: 'rgba(212,175,55,0.06)',
                        }}
                    >
                        {t('legal.english_controlling_notice')}
                    </div>
                )}
                {renderPolicy(body)}
            </div>
        </div>
    );
}
